import {
  Accordion,
  AccordionBody,
  Button,
  Column,
  Drawer,
  StickyFooterButtons,
  Typography,
  useLoading,
} from "best-common-react";
import React, { useEffect, useState } from "react";
import { saveEventOverrides } from "../../api/RequesTixApi";
import { defaultAccess, defaultCutoff } from "../../constants/CutoffConstants";
import { DepartmentByGameDTO } from "../../types/Department";
import { convertToEventTypeDTO } from "../../util/CutoffUtil";
import PersonalOnOff from "../requests/personal/PersonalOnOff";
import EventAccessAndCutoff from "./EventAccessAndCutoff";

type EventAccessBulkOverrideProps = {
  isOpen: boolean;
  onClose: () => void;
  onSave: () => void | Promise<void>;
  departments: DepartmentByGameDTO[];
  selectedDepartmentIds?: number[];
  gamePk: number | string;
  timezone?: string;
  personalRequestsOverride: boolean | null;
  setPersonalRequestsOverride: (value: boolean | null) => void;
  accessOverride: any;
  setAccessOverride: (value: any) => void;
  cutoffOverride: any;
  setCutoffOverride: (value: any) => void;
};

const EventAccessBulkOverrideDrawer = ({
  isOpen,
  onClose,
  onSave,
  departments,
  selectedDepartmentIds = [],
  gamePk,
  timezone,
  personalRequestsOverride,
  setPersonalRequestsOverride,
  accessOverride,
  setAccessOverride,
  cutoffOverride,
  setCutoffOverride,
}: EventAccessBulkOverrideProps) => {
  const { setLoading } = useLoading();
  const [canSave, setCanSave] = useState<boolean>(false);

  const selectedDepartments: DepartmentByGameDTO[] = departments.filter((dept: DepartmentByGameDTO) =>
    selectedDepartmentIds.includes(dept.departmentId),
  );

  const save = async () => {
    try {
      const access = accessOverride.active ? accessOverride : null;
      const cutoff = cutoffOverride.active ? cutoffOverride : null;

      setLoading(true);
      await saveEventOverrides({
        departmentIds: selectedDepartmentIds,
        gamePk: Number(gamePk),
        personalRequestsOverride,
        accessOverride: access,
        cutoffOverride: convertToEventTypeDTO(cutoff),
      });

      if (onSave) {
        onSave();
      }
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      setAccessOverride(defaultAccess);
      setCutoffOverride(defaultCutoff);
      setPersonalRequestsOverride(null);
    }
  }, [isOpen]);

  useEffect(() => {
    const ableToSave =
      selectedDepartmentIds.length > 0 &&
      (accessOverride.active || cutoffOverride.active || personalRequestsOverride != null);
    setCanSave(ableToSave);
  }, [accessOverride, cutoffOverride, personalRequestsOverride, selectedDepartmentIds]);

  return (
    <>
      {isOpen && (
        <Drawer isOpen={isOpen} size={500} onClose={onClose}>
          <div className="bcr-mb-2">
            <Typography variant="titleLarge">Bulk Override Ticket Access</Typography>
          </div>
          <div className="bcr-p-1">
            <div className="bcr-mb-3">
              <Accordion accordionKey="selected-departments-edit" startOpen={selectedDepartments.length <= 10}>
                <Accordion.Header>
                  <Typography elementStyles={{ weight: "bold" }}>
                    {selectedDepartments.length} Department(s) Selected
                  </Typography>
                </Accordion.Header>
                <AccordionBody>
                  <Typography>
                    {selectedDepartments.map((department) => department.departmentName).join(", ")}
                  </Typography>
                </AccordionBody>
              </Accordion>
            </div>
            <div className="bcr-my-2">
              <Column width={2}>
                <PersonalOnOff
                  value={personalRequestsOverride}
                  onChange={(value) => setPersonalRequestsOverride(value)}
                />
              </Column>
            </div>
            <EventAccessAndCutoff
              accessOverride={accessOverride}
              setAccessOverride={setAccessOverride}
              cutoffOverride={cutoffOverride}
              setCutoffOverride={setCutoffOverride}
              timezone={timezone}
            />
          </div>
          <StickyFooterButtons justifyContent="start" startsOpen={true}>
            <Button variant="primary" className="bcr-mx-3" onClick={save} disabled={!canSave}>
              Save Overrides
            </Button>
            <Button variant="default" onClick={onClose}>
              Cancel
            </Button>
          </StickyFooterButtons>
        </Drawer>
      )}
    </>
  );
};

export default EventAccessBulkOverrideDrawer;
